/**
 * Chip: the one small labelled shape of the cockpit, in one part. A `look` (a typed variant), a `size`, an optional
 * `count` after the label and a `state` (plain, selected, or a toggle that presses). The looks:
 *  - outline: a hairline round the words (the reviewer chip, "no fence", a count the rule is not, a tally that does
 *    not lead), in the primary ink or, for the quietest, the secondary;
 *  - ink: the dark ground the chip that leads wears (the leading tally, a box's kind, the reader of record, the
 *    chosen reason, the rule's own kind);
 *  - raised: a count on a raised ground, and fact: the quiet fact on a raised rectangle (a card's "lane · Document AI");
 *  - tone: a status that waits on a person (amber), is approved (green) or is a draft (blue);
 *  - wash: a paint's own text on a 15% wash of it (the done screens' tags);
 *  - paint: the stratum chip: the paint's text and border on a wash of it, 20 tall (compliance, tier, absence);
 *  - dashed: the chip that offers to add ("+ room / occupancy"): a dashed hairline on nothing, quieter than the facts beside it;
 *  - link: the outlined chip that is a link into another screen (`ChipLink` wears it).
 * A selected chip is not told by its ground alone: it wears `aria-current` and says "selected" to assistive
 * technology, and its weight steps up. A toggle is a real button (aria-pressed), its press the reviewer's pick.
 */
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { Button } from 'react-aria-components';
import { CHIP, CONTROL, RADIUS, SPACE, TEXT, WASH_ALPHA, WEIGHT, tint, usePalette, type Hex, type Palette } from '../theme';
import { focusRing } from './action-button';
import { Hint } from './hint';

/** A status chip's tone: amber while it waits on a person, green approved, blue a draft. */
export type ChipTone = 'waiting' | 'approved' | 'draft';

export type ChipLook =
  | { readonly kind: 'outline'; readonly ink: 'primary' | 'secondary' }
  | { readonly kind: 'ink' }
  | { readonly kind: 'raised' }
  | { readonly kind: 'fact' }
  | { readonly kind: 'tone'; readonly tone: ChipTone }
  | { readonly kind: 'wash'; readonly paint: Hex; readonly ink: Hex }
  | { readonly kind: 'paint'; readonly paint: Hex }
  | { readonly kind: 'dashed' }
  | { readonly kind: 'link' };

/** The chip sizes: a list row's badge, a bar's pill, a choice among several (a deny reason). */
export type ChipSize = 'row' | 'bar' | 'choice';

/** What a chip is for the reviewer: just shown, the one selected of its row, a toggle that presses, or a disclosure that opens something (announced as expanded, not pressed). */
export type ChipState =
  | { readonly kind: 'plain' }
  | { readonly kind: 'selected' }
  | { readonly kind: 'toggle'; readonly pressed: boolean; readonly onPress: () => void }
  | { readonly kind: 'disclosure'; readonly expanded: boolean; readonly onPress: () => void };

export const OUTLINE: ChipLook = { kind: 'outline', ink: 'primary' };
export const OUTLINE_QUIET: ChipLook = { kind: 'outline', ink: 'secondary' };
export const INK: ChipLook = { kind: 'ink' };
export const RAISED: ChipLook = { kind: 'raised' };
export const FACT: ChipLook = { kind: 'fact' };
export const LINK: ChipLook = { kind: 'link' };
export const DASHED: ChipLook = { kind: 'dashed' };
export const PLAIN: ChipState = { kind: 'plain' };
export const SELECTED: ChipState = { kind: 'selected' };

/** A status chip's look. */
export function toneOf(tone: ChipTone): ChipLook {
  return { kind: 'tone', tone };
}

/** The ground and ink a status wears. */
function toneInks(p: Palette, tone: ChipTone): { readonly ground: string; readonly ink: string } {
  switch (tone) {
    case 'waiting':
      return { ground: p.absenceWash, ink: p.absenceInk };
    case 'approved':
      return { ground: p.approvedWash, ink: p.approvedInk };
    case 'draft':
      return { ground: p.blueWash, ink: p.blueInk };
  }
}

/** The ground, ink, border and weight a look wears. */
function paints(p: Palette, look: ChipLook): { readonly ground: string; readonly ink: string; readonly border: string; readonly weight: number } {
  switch (look.kind) {
    case 'outline':
      return { ground: 'transparent', ink: look.ink === 'primary' ? p.ink : p.inkSecondary, border: p.hairline, weight: WEIGHT.regular };
    case 'ink':
      return { ground: p.inkStrong, ink: p.surface, border: p.inkStrong, weight: WEIGHT.medium };
    case 'raised':
      return { ground: p.surfaceRaised, ink: p.ink, border: 'transparent', weight: WEIGHT.strong };
    case 'fact':
      return { ground: p.surfaceRaised, ink: p.ink, border: 'transparent', weight: WEIGHT.regular };
    case 'tone': {
      const { ground, ink } = toneInks(p, look.tone);
      return { ground, ink, border: 'transparent', weight: WEIGHT.strong };
    }
    case 'wash':
      return { ground: tint(look.paint, WASH_ALPHA.rest), ink: look.ink, border: 'transparent', weight: WEIGHT.strong };
    case 'paint':
      return { ground: tint(look.paint, WASH_ALPHA.rest), ink: look.paint, border: look.paint, weight: WEIGHT.medium };
    case 'dashed':
      return { ground: 'transparent', ink: p.inkSecondary, border: p.inkFaint, weight: WEIGHT.regular };
    case 'link':
      return { ground: 'transparent', ink: p.blue, border: p.hairline, weight: WEIGHT.regular };
  }
}

/** The radius a look is cut with: a pill, but the raised fact and the link are the md rectangle and the stratum chip the small one. */
function radiusOf(look: ChipLook): number {
  switch (look.kind) {
    case 'fact':
    case 'dashed':
    case 'link':
      return RADIUS.md;
    case 'paint':
      return RADIUS.sm;
    case 'outline':
    case 'ink':
    case 'raised':
    case 'tone':
    case 'wash':
      return RADIUS.pill;
  }
}

/** The type of a size. */
function textOf(size: ChipSize): number {
  switch (size) {
    case 'row':
      return TEXT.micro;
    case 'bar':
      return TEXT.caption;
    case 'choice':
      return TEXT.control;
  }
}

/** A sized chip's padding, `padY padX`. */
function chipPadding(size: ChipSize): string {
  switch (size) {
    case 'row':
      return `${CHIP.row.padY}px ${CHIP.row.padX}px`;
    case 'bar':
      return `${CHIP.bar.padY}px ${CHIP.bar.padX}px`;
    case 'choice':
      return `${CHIP.choice.padY}px ${CHIP.choice.padX}px`;
  }
}

/** The shape a chip is drawn in (the menu's trigger wears it too): the look's paints, the size's padding, one line. */
export function chipStyle(p: Palette, look: ChipLook, size: ChipSize, selected: boolean): CSSProperties {
  const { ground, ink, border, weight } = paints(p, look);
  const stratum = look.kind === 'paint';
  return {
    display: 'inline-flex',
    alignItems: stratum ? 'center' : 'baseline',
    gap: stratum ? SPACE.xs : SPACE.tight,
    ...(stratum ? { height: CHIP.paintHeight, padding: `0 ${CONTROL.padXChip}px`, lineHeight: 1 } : { padding: chipPadding(size), lineHeight: 'normal' }),
    ...(look.kind === 'wash' ? { alignSelf: 'flex-start' } : {}),
    borderRadius: radiusOf(look),
    border: `1px ${look.kind === 'dashed' ? 'dashed' : 'solid'} ${border}`,
    background: ground,
    color: ink,
    font: 'inherit',
    fontSize: stratum ? TEXT.caption : textOf(size),
    fontWeight: selected ? Math.max(weight, WEIGHT.strong) : weight,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };
}

/** Said to assistive technology only: the chip's state, which its ground alone would tell the sighted. */
const SAID: CSSProperties = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };

export function Chip({
  look,
  size = 'bar',
  count,
  state = PLAIN,
  children,
}: {
  look: ChipLook;
  size?: ChipSize;
  /** A figure after the label, in the quieter weight and ink. */
  count?: ReactNode;
  state?: ChipState;
  children: ReactNode;
}) {
  const p = usePalette();
  const selected = state.kind === 'selected';
  const lead = look.kind === 'ink';
  const base = chipStyle(p, look, size, selected);
  const body = (
    <>
      {children}
      {count !== undefined && <span style={{ fontWeight: WEIGHT.regular, color: lead ? p.inkFaint : p.inkMuted }}>{count}</span>}
      {selected && <span style={SAID}>, selected</span>}
    </>
  );
  if (state.kind === 'toggle' || state.kind === 'disclosure') {
    const announced = state.kind === 'toggle' ? { 'aria-pressed': state.pressed } : { 'aria-expanded': state.expanded };
    return (
      <Button
        onPress={state.onPress}
        {...announced}
        style={(s) => ({
          ...base,
          position: 'relative',
          background: s.isHovered && !lead ? p.surfaceRaised : base.background,
          cursor: 'pointer',
          outline: 'none',
          boxShadow: s.isFocusVisible ? focusRing(p) : undefined,
        })}
      >
        {body}
      </Button>
    );
  }
  return (
    <span aria-current={selected ? 'true' : undefined} style={{ ...base, position: 'relative' }}>
      {body}
    </span>
  );
}

/** The outlined chip that is a link into another screen: its own address stays, so a middle click still lands. */
export function ChipLink({
  to,
  params,
  search,
  onClick,
  children,
}: {
  to: LinkProps['to'];
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  /** A press that may take over the navigation (it calls preventDefault) while the link keeps its own address. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}) {
  const p = usePalette();
  return (
    <Link
      to={to}
      params={params}
      search={search}
      onClick={onClick}
      style={{ ...chipStyle(p, LINK, 'bar', false), textDecoration: 'none' }}
    >
      {children}
    </Link>
  );
}

/** The paint wash chip, called by its paint and the deeper ink the wash needs (the done screens' tag, INGESTED). */
export function Pill({ paint, ink, children }: { paint: Hex; ink?: Hex; children: ReactNode }) {
  return <Chip look={{ kind: 'wash', paint, ink: ink ?? paint }}>{children}</Chip>;
}

/** The stratum chip: a data stratum's paint on a wash, with an optional hint over it (compliance, tier, absence). */
export function StrataChip({ paint, title, children }: { paint: Hex; title?: ReactNode; children: ReactNode }) {
  const chip = <Chip look={{ kind: 'paint', paint }}>{children}</Chip>;
  return title ? <Hint title={title}>{chip}</Hint> : chip;
}
